import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams } from "react-router-dom"

import { Text, Table, Card, Container, Anchor, ActionIcon, ScrollArea, Group, Skeleton, Pagination, Badge, Checkbox, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCircleCheck, IconFilePencil, IconUserCircle, IconChartLine, IconSearch } from '@tabler/icons';
import { GET_ADMIN_USERS } from '../../../graphql/queries';
import { Error } from '../../../Components/Error';
import { EditUserPlanModal } from '../../../Components/Admin/EditUserPlanModal';
import { CountryFlag } from '../../../Components/CountryFlag';
import { Link } from 'react-router-dom';
import { DateTime } from "luxon";


const DATE_FORMAT = "yyyy/LL/dd"

export function AdminUsersPage() {
  const perPage = 25;
  const [ params, setParams ] = useSearchParams();

  const page = parseInt(params.get("page") || "1");
  const payed = params.get("payed") === "true";
  const active = params.get("active") === "true";
  const blocked = params.get("blocked") === "true";
  const email = params.get("email") || "";
  const userId = params.get("uid") || "";

  const [emailInput, setEmailInput] = useState(email);
  const [userIdInput, setUserIdInput] = useState(userId);
  const [debouncedEmail] = useDebouncedValue(emailInput, 400);
  const [debouncedUserId] = useDebouncedValue(userIdInput, 400);

  type FilterState = {
    page: number,
    payed: boolean,
    active: boolean,
    blocked: boolean,
    email: string,
    userId: string
  };

  const filters: FilterState = { page, payed, active, blocked, email, userId };

  const buildParams = (state: FilterState) => {
    const next: Record<string, string> = { page: state.page.toString(), payed: state.payed ? "true" : "false" };
    if (state.active) {
      next.active = "true";
    }
    if (state.blocked) {
      next.blocked = "true";
    }
    if (state.email) {
      next.email = state.email;
    }
    if (state.userId) {
      next.uid = state.userId;
    }
    return next;
  }

  useEffect(() => {
    if (debouncedEmail === email && debouncedUserId === userId) {
      return;
    }
    // filter change resets pagination to the first page
    setParams(buildParams({ ...filters, page: 1, email: debouncedEmail, userId: debouncedUserId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail, debouncedUserId, email, userId, payed, active, blocked, setParams]);

  const setPage = (page: number) => {
    setParams(buildParams({ ...filters, page }));
  }

  const setFilter = (changes: Partial<FilterState>) => {
    setParams(buildParams({ ...filters, page: 1, ...changes }));
  }

  const queryVariables = {
    offset: (page - 1) * perPage,
    limit: perPage,
    isPayed: payed,
    email: email || null,
    userId: userId || null,
    isActive: active,
    isBlocked: blocked
  };

  const { loading, error, data } = useQuery(GET_ADMIN_USERS, {
    variables: queryVariables
  });
  const [editingUser, setEditingUser] = useState<any | null>(null);
  let rows = null;

  let plans = [];
  let plansBySlug: Record<string, any> = {};
  if (data) {
    for (const plan of data.adminPlans) {
      plansBySlug[plan.slug] = plan;
      plans.push(plan.slug);
    }
    rows = data.adminUsers.list.map((row: any) => {
      const planInfo = row.data.planSlug ? plansBySlug[row.data.planSlug] : null;
      return (
        <tr key={row.id}>
          <td>
            <Anchor
              component={Link}
              target="_blank"
              to={`https://fusionauth.tronql.com/admin/user/manage/${row.id}`}
            >
              {row.email}
            </Anchor>
            <Text color="dimmed">{row.id}</Text>
            <Text color="dimmed">Created: {DateTime.fromISO(row.insertInstant).toFormat(DATE_FORMAT)}</Text>
            {row.data.telegramUsername && <Text color="dimmed"><IconUserCircle size={16} stroke={1.5} />{row.data.telegramUsername}</Text>}
            {row.latestUsedIp && (
              <Text color="dimmed">
                <CountryFlag code={row.latestUsedIpCountry} /> {row.latestUsedIp}
              </Text>
            )}
          </td>
          <td>
            {row.active ? <IconCircleCheck /> : "-"}
            {row.verified ? <IconCircleCheck /> : "-"}
            {row.isAdmin ? <IconCircleCheck /> : "-"}
          </td>
          <td>
            {row.data.planSlug}
            {planInfo && (
              <Text color="dimmed">{planInfo.price != null ? parseFloat(planInfo.price) : 'Free'} {planInfo.currency}</Text>
            )}
            {row.data.planEndDate && (
              <div>
                End date: {DateTime.fromISO(row.data.planEndDate).toFormat(DATE_FORMAT)}
              </div>
            )}
            {row.data.blockedTill && (
              <div>
                Blocked: {DateTime.fromISO(row.data.blockedTill).toFormat(DATE_FORMAT)}
              </div>
            )}
          </td>
          <td>
            {row.data.billingBeginDate && (
              <>
                {DateTime.fromISO(row.data.billingBeginDate).toFormat(DATE_FORMAT)}
              </>
            )}
          </td>
          <td>
            {row.currentStats && (
              <>
                <div style={{whiteSpace: "nowrap"}}>
                  <Badge>{DateTime.fromISO(row.currentStats.begin).toFormat(DATE_FORMAT)}</Badge> -
                  <Badge>{DateTime.fromISO(row.currentStats.end).toFormat(DATE_FORMAT)}</Badge>
                </div>
                <div>Total: {row.currentStats.total.toLocaleString()}</div>
                {row.currentStats.isBlocked && <Badge color="red">Blocked</Badge>}
                <Badge color={row.currentStats.isActive ? "green" : "gray"}>{row.currentStats.isActive ? "Active" : "Inactive"}</Badge>
              </>
            )}
          </td>
          <td>
            <Group spacing={0} position="right">
              <ActionIcon component={Link} to={`/admin/users/${row.id}/view`} color="blue">
                <IconChartLine size={16} stroke={1.5} />
              </ActionIcon>
              <ActionIcon onClick={() => setEditingUser(row)} color="green">
                <IconFilePencil size={16} stroke={1.5} />
              </ActionIcon>
            </Group>
          </td>
        </tr>
      );
    });
  }
  return (
    <>
      <EditUserPlanModal
        user={editingUser}
        plans={plans}
        refetchQueries={[{ query: GET_ADMIN_USERS, variables: queryVariables }]}
        onClose={() => setEditingUser(null)}
      />

      <Group align="flex-end">
        <h2 style={{ flex: 1 }} >Users{data ? `, total ${data.adminUsers.count}` : ''}</h2>
        <TextInput
          my={20}
          icon={<IconSearch size="1rem" />}
          placeholder="Filter by email"
          value={emailInput}
          onChange={(event) => setEmailInput(event.currentTarget.value)}
        />
        <TextInput
          my={20}
          icon={<IconSearch size="1rem" />}
          placeholder="Filter by user id"
          value={userIdInput}
          onChange={(event) => setUserIdInput(event.currentTarget.value)}
        />
      </Group>

      <Group>
        Filters:
        <Checkbox label="Payed" checked={payed} onChange={(event) => setFilter({ payed: event.currentTarget.checked })} />
        <Checkbox label="Active" checked={active} onChange={(event) => setFilter({ active: event.currentTarget.checked })} />
        <Checkbox label="Blocked" checked={blocked} onChange={(event) => setFilter({ blocked: event.currentTarget.checked })} />
      </Group>

      { loading ? (
        <>
          <Skeleton height={8} mt={6} radius="xl" />
          <Skeleton height={8} mt={6} radius="xl" />
          <Skeleton height={8} mt={6} radius="xl" />
        </>
      ) : (
        <>
        { error ? (
          <Error text={error?.toString()} />
        ) : (
          <>
            <Container>
              { rows && rows.length ? (
                <ScrollArea type="auto">
                  <Table
                    sx={(theme) => ({
                      minWidth: 800,
                      [theme.fn.smallerThan('sm')]: {
                        minWidth: 600,
                      },
                    })}
                    verticalSpacing="xs"
                  >
                    <thead>
                      <tr>
                        <th>Email/UserId</th>
                        <th>Active/Verified/Admin</th>
                        <th>Plan/Expired/Blocked&nbsp;Till</th>
                        <th>Billing start</th>
                        <th>Stats</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                  </Table>
                </ScrollArea>
              ) : (
                <Card>{email || userId || payed || active || blocked ? 'No users match the current filters.' : 'No users.'}</Card>
              )}
              <Pagination value={page} total={Math.ceil(data.adminUsers.count / perPage)} onChange={setPage} />
            </Container>
          </>
        )}
        </>
      )}
    </>
  );
}